/* =============================================================================
   CORE / ORDER-ITEMS-CACHE — EINE Item-Single-Source für alle Displays
   =============================================================================
   Session 104 / S104-A (Refactor)
   ----------------------------------------------------------------------------
   Hintergrund (Memory `gg-storage-orders-list-no-items`): `_state.orders` wird
   über `mapOrderRow` (public/adapters/storage.js) gebaut — OHNE `order_items`,
   `preorderSnapshot` default `{}`. Die Positionen liegen autoritativ in der
   `order_items`-Tabelle und werden über `orders.listItems(id)` /
   `orders.listItemsForOrderIds(ids)` (beide camelCase) gelesen.

   Bis S103 hatte jede Oberfläche ihren EIGENEN Item-Zugriff:
     - orders.js  : Felder AM Order-Objekt (`_rawItems`/`_itemsSnapshot`) + ein
                    Einmal-Guard `_itemsEnrichAttempted`.
     - kds.js     : modul-`_itemsCache` (Map orderId→items)
     - bar.js     : modul-`_itemsCache`
     - delivery.js: modul-`_itemsCache`
   Der orders.js-Weg war fragil (S101-Live-Befund): der Realtime-Re-Sync ersetzt
   `_state.orders[idx]` durch ein frisch gemapptes Objekt → Client-only-Felder
   (`_rawItems`) werden gewischt; danach blockierte der Einmal-Guard das
   Neuladen → Kanal-Pills fielen auf „beide" zurück.

   Dieses Modul ist die EINE Quelle: ein modul-level Cache (keyed by orderId,
   überlebt den Realtime-Re-Sync), ein Batch-Loader, plus `orderChannels`. Alle
   Displays (operative Kachel, KDS, Theke, Liefer-Cockpit) beziehen ihre
   Positionen hierüber → gleiche Daten, gleiche Funktion, gleiche Erfahrung.

   Robust gegen Realtime:
     - Cache keyed by orderId (nicht am Order-Objekt) → Re-Sync wischt nichts.
     - KEIN dauerhafter Einmal-Guard: Cache-Präsenz + Inflight sind der Guard;
       `invalidate(id)` leert gezielt → der nächste Render lädt neu.
     - `order_items` sind nach Order-Anlage immutabel (Korrekturen erzeugen neue
       Orders) → für die Anzeige aktiver Orders ist keine Invalidierung nötig;
       die Displays invalidieren defensiv über ihre Realtime-Handler.
============================================================================ */

import { isDrinkItem } from './display-config.js';

// orderId (String) → items[] (camelCase order_items-Rows). Leeres Array = „kein
// order_items" (z.B. reine Snapshot-Order) — bewusst gecacht, damit kein
// Re-Fetch-Loop entsteht.
const _cache = new Map();
const _inflight = new Map(); // orderId → Promise

function _ordersStorage() {
  return (typeof window !== 'undefined' && window.App && window.App.storage
          && window.App.storage.orders) || null;
}

/** Snapshot-Fallback (Web-Sofortanzeige, bevor order_items geladen sind):
 *  v2 `{items:[...]}` ODER legacy v1 flat-Array. Diese Items tragen `category`
 *  (menu-picker), `isDrinkItem` erkennt sie. */
function snapshotItems(order) {
  const snap = order && (order.preorderSnapshot || order.preorder_snapshot);
  if (Array.isArray(snap)) return snap;
  if (snap && Array.isArray(snap.items)) return snap.items;
  return [];
}

/** Gruppiert die Rückgabe von `listItemsForOrderIds` nach orderId. Die
 *  Storage-Impl liefert eine FLACHE Row-Liste (jede Row mit `.orderId`); ältere
 *  Display-Caches behandelten sie fälschlich als `{id:[...]}`-Map (toter Batch,
 *  maskiert durch den per-Order-Fallback). Hier defensiv beide Formen. */
function _groupByOrderId(result, ids) {
  const out = {};
  for (const id of ids) out[id] = [];
  if (Array.isArray(result)) {
    for (const row of result) {
      const oid = String((row && (row.orderId || row.order_id)) || '');
      if (!oid) continue;
      if (!out[oid]) out[oid] = [];
      out[oid].push(row);
    }
  } else if (result && typeof result === 'object') {
    for (const id of ids) {
      const v = result[id];
      out[id] = Array.isArray(v) ? v : [];
    }
  }
  return out;
}

/** Rohe `order_items`-Rows aus dem Cache (oder `undefined`, wenn nie geladen).
 *  Für Render-Pfade, die selbst in einen Snapshot konvertieren. */
export function getCachedItems(orderId) {
  return _cache.get(String(orderId));
}

/** Ist für diese Order schon (mind. ein Versuch) im Cache? */
export function hasCachedItems(orderId) {
  return _cache.has(String(orderId));
}

/** Läuft für diese Order gerade ein Fetch (Batch oder Einzel)? S105 — der
 *  Re-Render-Pfad muss inflight-IDs überspringen, sonst entsteht ein
 *  Microtask-Loop: ein Render, der eine bereits inflight (aber noch nicht
 *  gecachte) Order „nachladen" will, bekommt von `batchFetchItemsFor` ein
 *  sofort resolved No-Op-Promise und re-rendert → erneut inflight → … bis der
 *  ursprüngliche Netz-Fetch endlich auflöst (Main-Thread-Starvation). */
export function hasPendingItems(orderId) {
  return _inflight.has(String(orderId));
}

/** Cache- ODER Inflight-Präsenz — der korrekte Guard für „muss ich für diese
 *  Order noch einen Fetch anstoßen?". */
export function hasCachedOrPendingItems(orderId) {
  const id = String(orderId);
  return _cache.has(id) || _inflight.has(id);
}

/**
 * Beste verfügbare Positionen einer Order — SYNCHRON, ohne Seiteneffekt.
 * `order_items` (Cache) gewinnt; sonst der Snapshot-Fallback. Shape-tolerant:
 * `isDrinkItem` + `orderChannels` lesen sowohl `order_items`-Rows
 * (`categorySnapshot`/`nameSnapshot`) als auch Snapshot-Items (`category`/`name`).
 */
export function itemsFor(order) {
  if (!order) return [];
  const cached = _cache.get(String(order.id));
  if (Array.isArray(cached) && cached.length) return cached;
  return snapshotItems(order);
}

/** Einzel-Fetch (dedupliziert über Inflight). */
export function fetchItemsFor(orderId) {
  const id = String(orderId || '');
  if (!id) return Promise.resolve([]);
  if (_cache.has(id)) return Promise.resolve(_cache.get(id));
  if (_inflight.has(id)) return _inflight.get(id);
  const s = _ordersStorage();
  if (!s || typeof s.listItems !== 'function') return Promise.resolve([]);
  const p = s.listItems(id)
    .then(items => {
      const list = Array.isArray(items) ? items : [];
      _cache.set(id, list);
      _inflight.delete(id);
      return list;
    })
    .catch(err => {
      console.warn('[order-items-cache] listItems failed for', id, err);
      _cache.set(id, []);
      _inflight.delete(id);
      return [];
    });
  _inflight.set(id, p);
  return p;
}

/** Batch-Fetch der fehlenden IDs in EINEM Request (mit N+1-Fallback).
 *  Gruppiert die flache Row-Liste korrekt nach orderId. */
export async function batchFetchItemsFor(orderIds) {
  const s = _ordersStorage();
  if (!s) return;
  const missing = (orderIds || [])
    .map(x => String(x || ''))
    .filter(id => id && !_cache.has(id) && !_inflight.has(id));
  if (!missing.length) return;

  if (typeof s.listItemsForOrderIds === 'function') {
    const batch = s.listItemsForOrderIds(missing)
      .then(result => {
        const grouped = _groupByOrderId(result, missing);
        for (const id of missing) {
          _cache.set(id, grouped[id] || []);
          _inflight.delete(id);
        }
      })
      .catch(err => {
        console.warn('[order-items-cache] batch failed, N+1 fallback:', err);
        return Promise.all(missing.map(id => { _inflight.delete(id); return fetchItemsFor(id); })).then(() => {});
      });
    for (const id of missing) _inflight.set(id, batch);
    return batch;
  }
  // Letzter Fallback: N+1
  return Promise.all(missing.map(id => fetchItemsFor(id))).then(() => {});
}

/** Cache gezielt (oder komplett) invalidieren — vom Realtime-Pfad der Displays.
 *  Ohne `orderId` → alles leeren. */
export function invalidateItems(orderId) {
  if (orderId == null) { _cache.clear(); _inflight.clear(); return; }
  const id = String(orderId);
  _cache.delete(id);
  _inflight.delete(id);
}

/**
 * Pure-Helper: betroffene Kanäle einer Order aus ihren Positionen. „Speise" =
 * jedes Nicht-Getränk (negativer Filter via `isDrinkItem`, identisch zu KDS:
 * `!isDrinkItem`, und Theke: `isDrinkItem`). Leere/unbekannte Liste → beide
 * `false`; der Aufrufer behält dann den Status quo (z.B. beide Pills), statt
 * einen Kanal fälschlich zu verstecken.
 */
export function orderChannels(items) {
  const list = Array.isArray(items) ? items : [];
  let hasFood = false, hasDrinks = false;
  for (const it of list) {
    if (isDrinkItem(it)) hasDrinks = true;
    else hasFood = true;
    if (hasFood && hasDrinks) break;
  }
  return { hasFood, hasDrinks };
}

/**
 * S135 P4 (B) — Positionen einer Order nach Bestell-Runde (`roundNo`) gruppieren.
 * Liefert nach Runde aufsteigend sortierte Gruppen `{ round, items }`. Stabil:
 * die Item-Reihenfolge innerhalb einer Runde bleibt erhalten (line_index-Sort
 * kommt aus listItems). Single-Source für KDS + Theke; ab Runde 2 rendern die
 * Views einen Batch-Header „Runde N". round_no ist reine Darstellung — der
 * Kanal-/Master-Status bleibt order-eben.
 */
export function groupItemsByRound(items) {
  const list = Array.isArray(items) ? items : [];
  const map = new Map();
  for (const it of list) {
    const r = Number(it && (it.roundNo != null ? it.roundNo : it.round_no)) || 1;
    if (!map.has(r)) map.set(r, []);
    map.get(r).push(it);
  }
  return [...map.keys()].sort((a, b) => a - b).map(r => ({ round: r, items: map.get(r) }));
}

/* =============================================================================
   S139 P5 — Runden-Kanal-Status-Cache (per (Runde × Kanal))
   =============================================================================
   Zweite, gleichgebaute Quelle neben dem Items-Cache: pro Order der aktuelle
   Produktions-Status JEDER Runde × Kanal (`order_round_channel_status`). KDS und
   Theke rendern damit eine eigene Kachel je (Order, Runde-mit-Speisen/Getränken)
   und schalten sie unabhängig fort — ohne Vorrunden anzufassen.

   Robustheit identisch zum Items-Cache (keyed by orderId, überlebt Realtime-
   Re-Sync, inflight-aware, gezielte Invalidierung). Fasst Abrechnung NICHT an —
   die neue Tabelle hängt nur an `round_no`, nie an `receipt_id`/invoice/TSE.
============================================================================ */

// orderId → rows[] (camelCase order_round_channel_status). [] = geladen, keine.
const _rcsCache = new Map();
const _rcsInflight = new Map();

export function getCachedRoundStatus(orderId) { return _rcsCache.get(String(orderId)); }
export function hasCachedRoundStatus(orderId) { return _rcsCache.has(String(orderId)); }
export function hasPendingRoundStatus(orderId) { return _rcsInflight.has(String(orderId)); }

/** Einzel-Fetch der Runden-Status-Zeilen einer Order (dedupliziert via Inflight). */
export function fetchRoundStatusFor(orderId) {
  const id = String(orderId || '');
  if (!id) return Promise.resolve([]);
  if (_rcsCache.has(id)) return Promise.resolve(_rcsCache.get(id));
  if (_rcsInflight.has(id)) return _rcsInflight.get(id);
  const s = _ordersStorage();
  if (!s || typeof s.listRoundChannelStatus !== 'function') return Promise.resolve([]);
  const p = s.listRoundChannelStatus(id)
    .then(rows => {
      const list = Array.isArray(rows) ? rows : [];
      _rcsCache.set(id, list);
      _rcsInflight.delete(id);
      return list;
    })
    .catch(err => {
      console.warn('[order-items-cache] listRoundChannelStatus failed for', id, err);
      _rcsCache.set(id, []);
      _rcsInflight.delete(id);
      return [];
    });
  _rcsInflight.set(id, p);
  return p;
}

/** Batch-Fetch der fehlenden Runden-Status-Zeilen (1 Request, N+1-Fallback). */
export async function batchFetchRoundStatusFor(orderIds) {
  const s = _ordersStorage();
  if (!s) return;
  const missing = (orderIds || [])
    .map(x => String(x || ''))
    .filter(id => id && !_rcsCache.has(id) && !_rcsInflight.has(id));
  if (!missing.length) return;
  if (typeof s.listRoundChannelStatusForOrderIds === 'function') {
    const batch = s.listRoundChannelStatusForOrderIds(missing)
      .then(result => {
        const grouped = _groupByOrderId(result, missing);
        for (const id of missing) {
          _rcsCache.set(id, grouped[id] || []);
          _rcsInflight.delete(id);
        }
      })
      .catch(err => {
        console.warn('[order-items-cache] round-status batch failed, N+1 fallback:', err);
        return Promise.all(missing.map(id => { _rcsInflight.delete(id); return fetchRoundStatusFor(id); })).then(() => {});
      });
    for (const id of missing) _rcsInflight.set(id, batch);
    return batch;
  }
  return Promise.all(missing.map(id => fetchRoundStatusFor(id))).then(() => {});
}

/** Runden-Status-Cache gezielt (oder komplett) invalidieren. */
export function invalidateRoundStatus(orderId) {
  if (orderId == null) { _rcsCache.clear(); _rcsInflight.clear(); return; }
  const id = String(orderId);
  _rcsCache.delete(id);
  _rcsInflight.delete(id);
}

/** Map `${round}|${channel}` → row (camelCase). Leere Map, wenn nichts gecacht. */
export function roundStatusFor(orderId) {
  const rows = _rcsCache.get(String(orderId)) || [];
  const m = new Map();
  for (const r of rows) {
    const round = Number(r && (r.roundNo != null ? r.roundNo : r.round_no)) || 1;
    const ch = String((r && (r.channel)) || '');
    if (ch) m.set(round + '|' + ch, r);
  }
  return m;
}

/** Die Status-Zeile einer konkreten (Order, Runde, Kanal) — oder null. */
export function roundChannelStatus(orderId, round, channel) {
  return roundStatusFor(orderId).get(Number(round) + '|' + String(channel)) || null;
}

// Rang pro Status — Single-Source für die Master-/Kanal-Aggregation (spiegelt
// den SQL-Helper _recompute_order_master_from_rounds, phase_107).
const _RCS_RANK = { pending: 0, preparing: 1, ready: 2 };

/**
 * Pure-Helper: Master-/Kanal-Status aus den Runden-Zeilen ableiten (min-rank
 * pending<preparing<ready über ALLE (Runde×Kanal)-Zeilen). Spiegelt die
 * DB-Aggregation; die DB bleibt autoritativ (über die orders-Realtime-Events) —
 * dieser Helper dokumentiert + testet die Invariante „ready erst, wenn ALLE
 * Runden/Kanäle ready". Rang-0 (irgendwo pending) → Master fällt auf die
 * „angenommen"-Basis zurück (confirmed; pending nur, wenn nie angenommen).
 * Liefert { kitchen, bar, master }; null, wo es keine Zeilen für den Kanal gibt.
 */
export function aggregateRoundStatus(rows, currentMaster) {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return { kitchen: null, bar: null, master: currentMaster || null };
  let minAll = Infinity, minKit = Infinity, minBar = Infinity;
  for (const r of list) {
    const rank = _RCS_RANK[r && r.status] != null ? _RCS_RANK[r.status] : 0;
    if (rank < minAll) minAll = rank;
    if (r && r.channel === 'kitchen' && rank < minKit) minKit = rank;
    if (r && r.channel === 'bar' && rank < minBar) minBar = rank;
  }
  const fromRank = (mn) => mn === Infinity ? null : (mn === 0 ? 'pending' : mn === 1 ? 'preparing' : 'ready');
  const master = minAll === 0
    ? (currentMaster === 'pending' ? 'pending' : 'confirmed')
    : (minAll === 1 ? 'preparing' : 'ready');
  return { kitchen: fromRank(minKit), bar: fromRank(minBar), master };
}

/* =============================================================================
   S245 T1 — Positionen, die sich OHNE Zutun dieses Cockpits geändert haben
   =============================================================================
   Befund aus dem S244-Retest: nach einer Gast-Nachbestellung (Runde 2) blieben
   Bestellkarte und Tisch-Warenkorb bei den Positionen von Runde 1, bis jemand
   neu lud. Die Summe stimmte sofort — sie hängt an `orders`.

   Die Wurzel ist eine Annahme, die bis Cut B stimmte: `order_items` änderte
   sich ausschliesslich durch Aktionen DIESES Cockpits, und die rufen
   `invalidateItems()` selbst. Es gibt deshalb bis heute **kein Realtime-Abo auf
   `order_items`** — nur auf `orders`, `order_round_channel_status`,
   `reservations`, die Archive und `table_occupancies`. Der Gast am Tisch hat
   diese Annahme gebrochen: er schreibt Positionen, ohne dass hier jemand klickt.
   (KDS zeigte Runde 2, weil es über `order_round_channel_status` eine eigene
   Quelle hat und dort invalidiert.)

   Ein eigenes Abo auf `order_items` wäre die naheliegende Antwort und ist die
   schlechtere: die Tabelle hat **keine `restaurant_id`**, ein Kanal liesse sich
   also nicht je Mandant filtern.

   Stattdessen der Umstand, den jede Positionsänderung ohnehin auslöst:
   `recompute_order_totals` schreibt die Summen auf `orders` zurück, und `orders`
   hat seit phase_78 `REPLICA IDENTITY FULL` — der UPDATE-Payload trägt also
   `old` UND `new`. Ändern sich die Summen, haben sich die Positionen geändert.
============================================================================ */

/** Felder, deren Änderung eine Positionsänderung beweist. `discount_cents`
 *  gehört dazu: ein Rabatt ändert die Summen, ohne dass eine Zeile dazukommt —
 *  die Positionsanzeige (Rabatt-Zeile) hängt trotzdem daran. */
const _TOTALS_FIELDS = [
  'total_gross_cents', 'subtotal_net_cents',
  'tax_7_amount_cents', 'tax_19_amount_cents', 'discount_cents'
];

/**
 * Pure — sagt ein Realtime-Payload auf `orders`, dass sich die POSITIONEN
 * geändert haben könnten?
 *
 * Bewusst konservativ in beide Richtungen:
 *  - ohne `old` (REPLICA IDENTITY nicht FULL, oder INSERT) → `false`. Ein
 *    „lieber einmal zu viel" hiesse: bei jedem Status-Klick alle Positionen
 *    aller sichtbaren Orders neu laden.
 *  - ändert sich nur `status`/`updated_at`, bleibt es ebenfalls bei `false` —
 *    dafür invalidieren die Cockpit-Aktionen selbst.
 *
 * @param {{eventType?:string, new?:object, old?:object}} payload
 * @returns {string|null} orderId, wenn invalidiert werden muss; sonst null
 */
export function orderIdWithChangedItems(payload) {
  if (!payload || payload.eventType !== 'UPDATE') return null;
  const nu = payload.new, old = payload.old;
  if (!nu || !old || !nu.id) return null;
  for (const f of _TOTALS_FIELDS) {
    // `!=` ist hier Absicht: der Payload liefert Zahlen mal als number, mal als
    // String (je nach Transport). Ein strikter Vergleich meldete jedes Mal eine
    // Änderung und träfe damit das Gegenteil des Gewollten.
    // eslint-disable-next-line eqeqeq
    if (nu[f] != old[f]) return String(nu.id);
  }
  return null;
}

/** Test-Hook: Cache + Inflight zurücksetzen (Items + Runden-Status). */
export function _resetItemsCacheForTests() {
  _cache.clear();
  _inflight.clear();
  _rcsCache.clear();
  _rcsInflight.clear();
}

/* `storage.js` ist ein globales Plain-Script ohne ESM-Importe und braucht genau
   diese drei Funktionen im Realtime-Pfad. */
if (typeof window !== 'undefined') {
  window.App = window.App || {};
  window.App.orderItemsCache = {
    invalidateItems,
    invalidateRoundStatus,
    orderIdWithChangedItems
  };
}
